import TextArea from "antd/es/input/TextArea";
import React, {useEffect, useState} from "react";
import {Button, Checkbox, Col, Dropdown, Flex, List, Modal, Popover, Row, Space} from "antd";
import {MenuDividerType} from "antd/es/menu/interface";
import {ItemType} from "rc-collapse/es/interface";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faUpRightAndDownLeftFromCenter,
    faCopy,
    faListCheck,
    faRetweet,
    faSpinner,
    faPencil, faIndent
} from "@fortawesome/free-solid-svg-icons";
import {aiIcon} from "./ModelsMenu";
import {aiChainPrompt} from "../Prompt";
import {useAppSelector} from "../../feature/tools";
import {useParams} from "react-router-dom";
import {assistantAsking} from "../Utils";
import {useDebounce} from "use-debounce";

export const TextAreaActions = (
    props: {
        value?: string,
        validate: (text: string) => boolean
        onChange: (text: string) => void
        onActionSelected?: () => boolean
        style?: React.CSSProperties,
        rows?: number,
        autoSize?: boolean,
        placeholder?: string,
        disabled?: boolean
    },
) => {
    const [text, setText] = useState(props.value)
    const [debouncedText] = useDebounce(text, 500);

    useEffect(() => {
        setText(props.value)
    }, [props.value]);

    useEffect(() => {
        if (text != props.value) {
            props.onChange(text ?? '')
        }
    }, [debouncedText]);

    const [selectedText, setSelectedText] = React.useState<{
        value: string,
        start: number,
        end: number,
        x: number,
        y: number
    }>()
    const [loading, setLoading] = React.useState(false)
    const [genResult, setGenResult] = React.useState<{ results: string[], action?: () => Promise<void> } | undefined>()
    const [genSummary, setGenSummary] = React.useState<string | undefined>()
    const model = useAppSelector(state => state.draftSlice.model)
    const [customPromptInput, setCustomPromptInput] = React.useState<{
        action?: (prompt: string, useContext: boolean) => Promise<void>
    } | undefined>(undefined)
    const [prompt, setPrompt] = React.useState<string | undefined>(undefined)
    const [useContext, setUseContext] = React.useState<boolean>(true)
    const {projectId} = useParams()
    //
    // useEffect(() => {
    //     setText(props.value)
    // }, [props.value]);

    const defaultMenuItems: ItemType[] = [
        {type: 'divider'} as MenuDividerType, {
            key: "append",
            label: "Append",
            style: {paddingRight: 6},
            // @ts-ignore
            icon: <FontAwesomeIcon icon={faIndent}/>
        }
    ]

    const loadingIcon = <FontAwesomeIcon icon={faSpinner} spin/>

    const editableAction = (title: string, action: () => void) =>
        <Flex
            gap={10}
            justify={'space-between'}
        >
            {title}
            <Button type={'text'}
                    size={'small'}
                    icon={<FontAwesomeIcon icon={faPencil}/>}
                    onClick={(e) => {
                        e.stopPropagation()
                        action()
                    }}/>
        </Flex>

    const extendAction = editableAction("Extend", () => {
        if (!prompt || prompt?.trim()?.length == 0) {
            setPrompt("Наполни текст деталями")
        }
        setCustomPromptInput({
            action: async (prompt: string) => {
                if (props.onActionSelected == undefined || props.onActionSelected()) {
                    setLoading(true)
                    setGenResult({results: []})
                    await runAction(prompt, useContext, 'extend', selectedText)
                    setLoading(false)
                }
            }
        })
    })

    const getActionsByType = (type: string) => {
        const header = [{
            key: "header",
            label: type.charAt(0).toUpperCase() + type.slice(1),
            disabled: true
        }, {type: 'divider'} as MenuDividerType] as ItemType[]
        switch (type) {
            case "word":
                return header.concat([{
                    key: "synonym",
                    label: "Synonym",
                    // @ts-ignore
                    icon: aiIcon
                }] as ItemType[])
            case "phrase":
                // @ts-ignore
                return header.concat([{
                    key: "re-phrase",
                    label: "Re-phrase",
                    // @ts-ignore
                    icon: aiIcon
                }, {
                    key: "extend",
                    label: extendAction,
                    style: {paddingRight: 6},
                    // @ts-ignore
                    icon: <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter}/>
                }] as ItemType[])
            case "sentence":
                // @ts-ignore
                return header.concat([{
                    key: "re-generate-sentence",
                    label: "Re-generate",
                    // @ts-ignore
                    icon: aiIcon
                }, {
                    key: "extend",
                    label: extendAction,
                    style: {paddingRight: 6},
                    // @ts-ignore
                    icon: <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter}/>
                }] as ItemType[])
            case "paragraph":
                // @ts-ignore
                return header.concat([{
                    key: "re-generate-paragraph",
                    label: "Re-generate",
                    // @ts-ignore
                    icon: aiIcon
                }, {
                    key: "extend",
                    label: extendAction,
                    style: {paddingRight: 6},
                    // @ts-ignore
                    icon: <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter}/>
                }] as ItemType[])
            case "section":
                return header.concat([
                    // @ts-ignore
                    {key: "re-generate-section", label: "Re-generate", icon: loading ? loadingIcon : aiIcon}, {
                        key: "extend",
                        label: extendAction,
                        style: {paddingRight: 6},
                        // @ts-ignore
                        icon: <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter}/>
                    },
                    {
                        key: "summary",
                        label: "Summary",
                        icon: <FontAwesomeIcon icon={faListCheck}/>
                    }
                ] as ItemType[])
        }
        return [] as ItemType[]
    }

    const getItems = (description?: string) => {
        if (!description) return []
        const paragraphs = description.replace("\n\n", "\n").split("\n").length
        const sentences = description.split(".").length - 1 + description.split("!").length - 1 + description.split("?").length - 1
        const words = description.split(" ").length
        const type = paragraphs > 1 ? 'section' : sentences > 1 ? 'paragraph' : sentences == 1 ? "sentence" : words > 1 ? "phrase" : "word"
        return getActionsByType(type).concat(!props.disabled && !loading ? defaultMenuItems : []).filter((item) => selectedText?.start != selectedText?.end || item.key == "append")
    }

    const runAction = async (prompt: string | undefined, useContext: boolean, key: string, selectedText: {
        value: string,
        start: number,
        end: number,
        x: number,
        y: number
    } | undefined): Promise<void> => {
        let cprompt = ""
        switch (key) {
            case "synonym":
                cprompt = `Подбери синонимы для слова.`
                break
            case "re-phrase":
                cprompt = `Перефразируй фразу.`
                break
            case "re-generate-sentence":
                cprompt = `Перепиши предложение.`
                break
            case "re-generate-paragraph":
                cprompt = `Перепиши параграф.`
                break
            case "re-generate-section":
                cprompt = `Перепиши секцию.`
                break
            case 'extend':
                cprompt = `${prompt != undefined && prompt.trim().length > 0 ? prompt : "Наполни текст деталями"}.`
                break
            case 'append':
                cprompt = `${prompt != undefined && prompt.trim().length > 0 ? (prompt + (useContext ? ('\nКонтекст: ' + text) : '')) : ("Дополни текст деталями.")}`
                break
            case "summary":
                cprompt = `Сгенерируй summary для секции.`
                break
        }
        try {
            let response = await aiChainPrompt({
                projectId: projectId ?? '',
                prompt: [
                    {role: "system", content: [{text: "Ты профессиоынальный писатель."}]},
                    {role: "user", content: [{text: `Нужно обработать текст`}]}
                ]
                    .concat(assistantAsking("Укажи исходный текст", useContext ? selectedText?.value : undefined))
                    .concat(assistantAsking("Укажи текст, который нужно обработать", selectedText?.value.substring(selectedText?.start, selectedText?.end).trim()))
                    .concat(assistantAsking("Укажи требования к результату", `
    ${key != "summary" && key != 'extend' && key != 'append' ? '-Предложи разные варианты' : ''}
    ${key != "summary" && key != 'extend' && key != 'append' ? '- Каждый результат должен быть уникален и не связан с другими результатами' : ''}
    - Результаты должны быть на том же языке, что и исходный текст
    - Результат должен быть в формате JSON и соответствовать схеме:
        ${key != "summary" && key != 'extend' && key != 'append' ? '{data: [string, string, string]}' : "{data: string}"}
`))
                    .concat(assistantAsking("Что нужно сделать с текстом", cprompt))
                ,
                model: model,
                type: "json"
            })

            if (key == "summary") {
                setGenSummary(response.data)
            } else {
                console.log(response.data)
                setGenResult({
                    results: key == 'extend' || key == 'append' ? [response.data] : response.data,
                    action: async () => {
                        await runAction(prompt, useContext, key, selectedText)
                    }
                })
            }
        } catch (e) {
            console.log(e);
        }
    }

    return (
        <>
            <Popover
                placement={'bottomRight'}
                content={
                    <Space direction={'vertical'}>
                        <TextArea onChange={(e) => setPrompt(e.target.value)}
                                  value={prompt}
                                  autoSize={true}
                                  style={{width: 300}}
                        />
                        <Checkbox checked={useContext} onChange={(e) => {
                            setUseContext(e.target.checked)
                        }}>Include context</Checkbox>
                        <Space><Button
                            type={'primary'}
                            onClick={async () => {
                                customPromptInput?.action?.(prompt ?? '', useContext)
                                setCustomPromptInput(undefined)
                            }}>Go</Button><Button type={'default'} onClick={() => {
                            setPrompt('')
                            setUseContext(true)
                        }}>Reset</Button></Space>
                    </Space>
                }
                title="Prompt"
                trigger="click"
                open={customPromptInput != undefined}
                onOpenChange={(e) => {
                    if (!e) setCustomPromptInput(undefined)
                }}
            >
                <Dropdown
                    autoAdjustOverflow={true}
                    trigger={['contextMenu']}
                    menu={{
                        // @ts-ignore
                        items: getItems(props.value?.substring(selectedText?.start, selectedText?.end).trim()),
                        onClick: async (item) => {
                            if (props.onActionSelected == undefined || props.onActionSelected()) {
                                setLoading(true)
                                if (item.key == "summary") {
                                    setGenSummary('')
                                    await runAction(prompt, useContext, item.key, selectedText)
                                } else if (item.key == "append") {
                                    setCustomPromptInput({
                                        action: async (prompt: string, useContext: boolean) => {
                                            setLoading(true)
                                            setGenResult({results: []})
                                            await runAction(prompt, useContext, item.key, selectedText)
                                            setLoading(false)
                                        }
                                    })
                                } else {
                                    setGenResult({results: []})
                                    await runAction(prompt, useContext, item.key, selectedText)
                                }

                                setLoading(false)
                            }
                        }
                    }}
                >
                    <TextArea
                        style={{...props.style, fontFamily: "PT Mono", fontSize: 14}}
                        rows={props.rows}
                        autoSize={props.autoSize}
                        value={text}
                        disabled={props.disabled || loading}
                        readOnly={props.disabled || loading}
                        placeholder={props.placeholder}
                        onSelect={(e: any) => {
                            if (!props.disabled && !loading && e.target.selectionStart != e.target.selectionEnd) {
                                setSelectedText({
                                    value: props.value ?? '',
                                    start: e.target.selectionStart,
                                    end: e.target.selectionEnd,
                                    x: e.nativeEvent.clientX,
                                    y: e.nativeEvent.clientY
                                })
                            } else {
                                setSelectedText(undefined)
                            }
                        }}
                        onChange={(e) => {
                            if (!props.disabled && !loading && props.validate(e.target.value)) {
                                setText(e.target.value)
                            }
                        }}/>
                </Dropdown>
            </Popover>
            <AIGenModal
                originalText={props.value}
                loading={loading}
                result={genResult}
                onClose={() => setGenResult(undefined)}
                selectedText={selectedText}
                onApply={(text: string) => {
                    setSelectedText(undefined)
                    if (props.validate(text)) {
                        setText(text)
                    }
                    setGenResult(undefined)
                }}
                onRetry={async () => {
                    setLoading(true)
                    await runAction(prompt, useContext, 'extend', selectedText)
                    setLoading(false)
                }}
            />
            <AISummaryModal
                loading={loading}
                summary={genSummary}
                onClose={() => setGenSummary(undefined)}
                onRetry={async () => {
                    setLoading(true)
                    await genResult?.action?.()
                    setLoading(false)
                }}
                selectedText={selectedText}
            />
        </>
    )
}

export const AIGenModal = (
    props: {
        originalText?: string
        loading?: boolean
        result?: { results: string[], action?: () => Promise<void> }
        onClose: () => void
        onRetry?: () => void
        onApply?: (text: string) => void
        selectedText?: {
            value: string,
            start: number,
            end: number,
            x: number,
            y: number
        }
    }) => {
    const [replacement, setReplacement] = useState<string | undefined>(props.selectedText?.value.substring(props.selectedText?.start, props.selectedText?.end).trim())

    useEffect(() => {
        setReplacement(props.selectedText?.value.substring(props.selectedText?.start, props.selectedText?.end).trim())
    }, [props.selectedText]);

    return (
        <Modal
            title={"AI Replacement"}
            open={props.result != undefined}
            okText={"Apply"}
            onOk={() => {
                const text = props.selectedText?.start == props.selectedText?.end ? ((props.originalText ?? '') + "\n\n" + replacement) : props.selectedText?.value.substring(0, props.selectedText?.start) + (replacement ?? '') + props.selectedText?.value.substring(props.selectedText?.end, props.selectedText?.value.length)
                props.onApply?.(text)
            }}
            onCancel={props.onClose}
            width={800}
        >
            <Row style={{maxHeight: 600, overflow: 'hidden'}} gutter={10}>
                <Col span={12} style={{
                    overflowY: 'auto',
                    fontFamily: 'PT Mono',
                    maxHeight: 600,
                }}>
                    <Flex justify={'space-between'}>
                        <Space>{props.loading ? <>{<FontAwesomeIcon icon={faSpinner} spin/>} Asking
                            AI...</> : ''}</Space>
                        <Space>
                            {<Button icon={<FontAwesomeIcon icon={faRetweet}/>}
                                     disabled={props.loading}
                                     onClick={props.onRetry}
                            />}
                        </Space>
                    </Flex>
                    <Button style={{
                        width: '100%',
                        textAlign: 'start',
                        margin: "5px 0",
                        height: 'auto',
                        textWrap: 'wrap',
                        fontStyle: 'italic',
                        fontFamily: 'PT Mono'
                    }}
                            type={'dashed'}
                            onClick={() => setReplacement(props.selectedText?.value.substring(props.selectedText?.start, props.selectedText?.end))}>
                        {props.selectedText?.value.substring(props.selectedText?.start, props.selectedText?.end)}
                    </Button>
                    <List
                        dataSource={props.result != undefined && props.result.results.length > 0 ? props.result?.results.map(item => ({text: item})) : []}
                        renderItem={(item) => {
                            return (<Button
                                style={{
                                    width: '100%',
                                    textAlign: 'start',
                                    margin: "5px 0",
                                    height: 'auto',
                                    textWrap: 'wrap',
                                    fontFamily: 'PT Mono',
                                    whiteSpace: 'pre-line'
                                }}
                                type={'text'}
                                onClick={() => setReplacement(item.text)}>{item.text}
                            </Button>)
                        }}
                    />
                </Col>
                <Col span={12}
                     style={{
                         whiteSpace: 'pre-line',
                         fontFamily: 'PT Mono',
                         overflowY: 'auto',
                         maxHeight: 600,
                     }}>{props.selectedText?.value.substring(0, props.selectedText?.start)}<span
                    style={{
                        background: "#5e5d5c",
                        padding: "3px 0"
                    }}>{replacement}</span>{props.selectedText?.value.substring(props.selectedText?.end, props.selectedText?.value.length)}
                </Col>
            </Row>
        </Modal>
    )
}

export const AISummaryModal = (
    props: {
        loading?: boolean
        summary?: string
        onClose: () => void
        onRetry?: () => void
        selectedText?: {
            value: string,
            start: number,
            end: number,
            x: number,
            y: number
        }
    }
) => {
    const [replacement, setReplacement] = useState<string | undefined>(props.selectedText?.value.substring(props.selectedText?.start, props.selectedText?.end).trim())

    useEffect(() => {
        setReplacement(props.selectedText?.value.substring(props.selectedText?.start, props.selectedText?.end).trim())
    }, [props.selectedText]);

    return (
        <Modal
            title={"AI Summary"}
            open={props.summary != undefined}
            onOk={props.onClose}
            onCancel={props.onClose}
            width={800}
        >

            <Row gutter={10} style={{maxHeight: 600, overflow: 'hidden'}}>
                <Col span={12}
                     style={{whiteSpace: 'pre-line', overflowY: 'auto', fontFamily: 'PT Mono', maxHeight: 600,}}>
                    <Space direction={'vertical'}>
                        <Flex justify={'space-between'}>
                            <Space>{props.loading ? <>{<FontAwesomeIcon icon={faSpinner} spin/>} Asking
                                AI...</> : ''}</Space>
                            <Space>

                                <Button
                                    disabled={props.loading}
                                    icon={<FontAwesomeIcon icon={faRetweet}/>}
                                    onClick={props.onRetry}/>
                                <Button disabled={props.loading}
                                        icon={<FontAwesomeIcon icon={faCopy}/>}
                                        onClick={() => navigator.clipboard.writeText(props.summary ?? '')}/>
                            </Space>
                        </Flex>
                        {props.summary}
                    </Space>
                </Col>
                <Col span={12}
                     style={{
                         whiteSpace: 'pre-line',
                         overflowY: 'auto',
                         fontFamily: 'PT Mono',
                         maxHeight: 600,
                     }}>{props.selectedText?.value.substring(0, props.selectedText?.start)}<span
                    style={{
                        background: "#ffdea2",
                        padding: "3px 0"
                    }}>{replacement}</span>{props.selectedText?.value.substring(props.selectedText?.end, props.selectedText?.value.length)}
                </Col>
            </Row>
        </Modal>
    )
}